import { useEffect, useRef, useCallback } from "react";

/**
 * Hook that calls `fn` on mount and then every `intervalMs` milliseconds.
 * Pauses when the tab is hidden (document.hidden) to save resources.
 * Returns a `refresh` function for manual immediate re-fetch.
 */
export function usePolling(fn: () => void | Promise<void>, intervalMs = 15_000) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const refresh = useCallback(() => {
    void fnRef.current();
  }, []);

  useEffect(() => {
    // Initial call handled by caller (most pages already do this)
    const id = setInterval(() => {
      if (!document.hidden) {
        void fnRef.current();
      }
    }, intervalMs);

    // Also refresh when tab becomes visible again
    const onVisibility = () => {
      if (!document.hidden) {
        void fnRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);

  return refresh;
}
